import { useEffect, useState } from "react";
import { api, type MatchMode, type MatchOptions, type RulesetSummary, type CoinTier, type LiveTable } from "../../net/api.js";
import { refreshSession, useSession } from "../../net/session.js";
import { createStore, useStore } from "../../net/store.js";
import { useActivity } from "../../app/ActivityController.js";
import { useNavigate } from "../../app/navigation.js";
import { mobileCopy } from "../../app/mobileCopy.js";
import { useLocale } from "../../i18n.js";
import { gameError } from "../../net/game-errors.js";

interface LobbyDraft { busy: boolean; error: string | null; sheet: "match" | "room" | null; roomTab: "create" | "join"; mode: MatchMode; rulesetId: string; coinTier: string; roomCode: string }
const draft = createStore<LobbyDraft>({ busy: false, error: null, sheet: null, roomTab: "create", mode: "ranked", rulesetId: "standard", coinTier: "practice", roomCode: "" });
function useDraft<K extends keyof LobbyDraft>(key: K): [LobbyDraft[K], (value: LobbyDraft[K]) => void] {
  const value = useStore(draft, (state) => state[key]);
  return [value, (next) => draft.set((state) => ({ ...state, [key]: next }))];
}
function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}


export function useLobby() {
  const { locale } = useLocale();
  const c = mobileCopy.bind(null, locale);
  const session = useSession();
  const me = session.status === "signedIn" ? session.me : null;
  const { activity, loading, refresh } = useActivity();
  const navigate = useNavigate();
  const offline = !useOnline();
  const [sheet, setSheet] = useDraft("sheet");
  const [roomTab, setRoomTab] = useDraft("roomTab");
  const [mode, setMode] = useDraft("mode");
  const [rules, setRules] = useState<RulesetSummary[]>([]);
  const [tiers, setTiers] = useState<CoinTier[]>([]);
  const [rulesetId, setRulesetId] = useDraft("rulesetId");
  const [coinTier, setCoinTier] = useDraft("coinTier");
  const [roomCode, setRoomCode] = useDraft("roomCode");
  const [busy, setBusy] = useDraft("busy");
  const [error, setError] = useDraft("error");
  const [now, setNow] = useState(Date.now());
  const [liveRooms, setLiveRooms] = useState<LiveTable[] | null>(null);

  useEffect(() => {
    void Promise.all([api.rulesets(), api.coinTiers()])
      .then(([r, t]) => {
        setRules(r);
        setTiers(t);

      })
      .catch((caught) => setError(gameError(caught, locale)));
  }, []);
  useEffect(() => {
    if (activity?.type !== "queue") return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [activity?.type]);
  useEffect(() => {
    let active = true;
    void api
      .liveTables()
      .then((result) => {
        if (active) setLiveRooms(result.tables);
      })
      .catch(() => {
        if (active) setLiveRooms([]);
      });
    return () => {
      active = false;
    };
  }, []);

  async function startMatch() {
    if (draft.get().busy) return;
    setBusy(true);
    setError(null);
    try {
      const options: MatchOptions = {
        mode,
        rulesetId,
        ...(mode === "gold" ? { coinTier } : {}),
      };
      const status = await api.joinQueue(options);
      await refresh();
      if (status.lobbyCode) navigate(`/room/${status.lobbyCode}`);
      setSheet(null);
    } catch (caught) {
      setError(gameError(caught, locale));
    } finally {
      setBusy(false);
    }
  }
  async function createRoom() {
    if (draft.get().busy) return;
    setBusy(true);
    setError(null);
    try {
      const room = await api.createLobby({ mode: "gold", rulesetId, coinTier });
      await refresh();
      navigate(`/room/${room.code}`);
    } catch (caught) {
      setError(gameError(caught, locale));
    } finally {
      setBusy(false);
    }
  }
  async function createParty() {
    if (draft.get().busy) return;
    setError(null);
    setBusy(true);
    try {
      const party = await api.createParty();
      await refresh();
      navigate(`/party/${party.id}`);
    } catch (caught) {
      setError(gameError(caught, locale));
    } finally {
      setBusy(false);
    }
  }
  async function joinRoom() {
    if (draft.get().busy) return;
    if (roomCode.trim().length < 4) { setError(locale === "zh-CN" ? "请输入完整房间码" : "Enter a complete room code"); return; }
    setBusy(true);
    try {
      await api.joinLobby(roomCode.trim());
      await refresh();
      navigate(`/room/${roomCode.trim().toUpperCase()}`);
    } catch (caught) {
      setError(gameError(caught, locale));
    } finally {
      setBusy(false);
    }
  }

  async function claimReward() {
    if (draft.get().busy) return;
    setBusy(true); setError(null);
    try { await api.claimDaily(); await refreshSession(); }
    catch (caught) { setError(gameError(caught, locale)); }
    finally { setBusy(false); }
  }

  async function cancelMatch() {
    if (draft.get().busy) return;
    setBusy(true); setError(null);
    try { await api.leaveQueue(); await refresh(); }
    catch (caught) { setError(gameError(caught, locale)); await refresh(); }
    finally { setBusy(false); }
  }
  return { locale, c, me, activity, loading, navigate, offline, sheet, setSheet, roomTab, setRoomTab, mode, setMode, rules, tiers, rulesetId, setRulesetId, coinTier, setCoinTier, roomCode, setRoomCode, busy, error, setError, now, liveRooms, startMatch, createRoom, createParty, joinRoom, cancelMatch, claimReward };
}
